class CartDiscountCalculator {
  constructor() {
    this.discountCodes = [];
    this.cartSubtotal = 0;
    this.cartItems = [];
    this.bestDiscount = null;
    this.debugMode = true;
  }

  log(message, data = null) {
    if (this.debugMode) {
      console.log(`[CartDiscount] ${message}`, data || '');
    }
  }

  async init() {
    this.log('Initializing Cart Discount Calculator...');
    this.getCartData();
    await this.fetchDiscountCodes();
    this.calculateBestDiscount();
    this.renderBestDiscount();
  }

  getCartData() {
    const cartData = window.cart || {};
    if (cartData.total_price != null) {
      this.cartSubtotal = Number(cartData.total_price) / 100;
    } else {
      const totalElement = document.querySelector('.totals__subtotal-value');
      if (totalElement) {
        const totalText = totalElement.textContent.replace(/[^0-9.,]/g, '').replace(/,/g, '');
        this.cartSubtotal = parseFloat(totalText) || 0;
      } else {
        this.cartSubtotal = 0;
      }
    }

    if (Array.isArray(cartData.items)) {
      this.cartItems = cartData.items.map(item => ({
        product_id: String(item.product_id ?? item.productId ?? item.product_id),
        price: Number(item.price ?? item.final_price ?? 0) / 100,
        collection_ids: Array.isArray(item.collection_ids) ? item.collection_ids.map(String) : []
      }));
    } else {
      this.cartItems = [];
    }

    this.log('Cart subtotal:', this.cartSubtotal);
    this.log('Cart items:', this.cartItems);
  }

  async fetchDiscountCodes() {
    this.log('Fetching discount codes...');
    if (window.discountCodes && Array.isArray(window.discountCodes)) {
      this.discountCodes = window.discountCodes;
      this.log('Loaded discount codes:', this.discountCodes);
      return;
    }
    this.log('No discount codes found');
  }

  toNumber(value, fallback = 0) {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'object' && value.value !== undefined) return Number(value.value) || fallback;
    return Number(value) || fallback;
  }

  getLinkedSets(discount) {
    const linkedProducts = (discount.linked_products || []).map(p =>
      typeof p === 'object' && p !== null ? String(p.id) : String(p)
    );
    const associatedCollections = (discount.associated_collections || []).map(c =>
      typeof c === 'object' && c !== null ? String(c.id) : String(c)
    );
    return { linkedProducts, associatedCollections };
  }

  calculateBestDiscount() {
    this.log('Calculating best discount for cart');
    let bestAmount = 0;
    let bestCode = null;

    this.discountCodes.forEach((discount, index) => {
      this.log(`Processing discount ${index + 1}:`, discount);

      const minValue = this.toNumber(discount.min_value, 0);
      const maxValue = this.toNumber(discount.max_value, 999999999);
      const discountType = discount.discount_type;
      const discountAmount = this.toNumber(discount.discount_amount, 0);
      const code = discount.code;

      const { linkedProducts, associatedCollections } = this.getLinkedSets(discount);
      const isGeneral = linkedProducts.length === 0 && associatedCollections.length === 0;

      let applicableAmountBase = 0;
      let applicable = false;

      if (isGeneral) {
        // General coupons apply to the subtotal within min/max
        if (this.cartSubtotal >= minValue && this.cartSubtotal <= maxValue) {
          applicable = true;
          applicableAmountBase = this.cartSubtotal;
        }
      } else {
        // Linked coupons: require ALL cart items to be eligible and in range
        const allItemsEligible = this.cartItems.length > 0 && this.cartItems.every(item => {
          const inRange = item.price >= minValue && item.price <= maxValue;
          const matchesProduct = linkedProducts.includes(String(item.product_id));
          const matchesCollection = associatedCollections.some(id => item.collection_ids.includes(String(id)));
          const matchesLink = matchesProduct || matchesCollection;
          return inRange && matchesLink;
        });

        if (allItemsEligible) {
          applicable = true;
          // Apply percentage on full subtotal when all items qualify; fixed is flat
          applicableAmountBase = this.cartSubtotal;
        }
      }

      if (!applicable) {
        this.log(`  - Not applicable`);
        return;
      }

      let calculatedDiscountAmount = 0;
      let discountLabel = '';
      if (discountType === 'percentage') {
        calculatedDiscountAmount = applicableAmountBase * (discountAmount / 100);
        discountLabel = `${discountAmount}% OFF`;
      } else if (discountType === 'fixed') {
        calculatedDiscountAmount = discountAmount;
        discountLabel = `₹${discountAmount} OFF`;
      }

      this.log(`  - Applicable base: ${applicableAmountBase}, savings: ${calculatedDiscountAmount}`);

      if (calculatedDiscountAmount > bestAmount) {
        bestAmount = calculatedDiscountAmount;
        bestCode = {
          code,
          label: discountLabel,
          amount: calculatedDiscountAmount,
          type: discountType,
          value: discountAmount
        };
        this.log('  - New best discount:', bestCode);
      }
    });

    this.bestDiscount = bestCode;
    this.log('Final best discount:', this.bestDiscount);
  }

  renderBestDiscount() {
    this.log('Rendering best discount...');
    const container = document.querySelector('.cart-best-discount-container');
    if (!container) {
      this.log('Cart best discount container not found');
      return;
    }

    if (this.bestDiscount) {
      const savingsAmount = Math.round(this.bestDiscount.amount);
      const originalSubtotal = Math.round(this.cartSubtotal);
      const finalSubtotal = Math.max(0, originalSubtotal - savingsAmount);

      container.innerHTML = `
        <div class="cart-best-discount-coupon" style="
          margin-bottom: 8px; 
          padding: 12px 16px; 
          background: #f0f8f0; 
          border: none;
          display: flex; 
          align-items: center; 
          justify-content: space-between;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        ">
          <div style="flex: 1;">
            <div style="
              color: #2e7d32; 
              font-weight: 600; 
              font-size: 16px; 
              margin-bottom: 2px;
            ">
              Get your products for ₹${finalSubtotal.toLocaleString()} with coupon
            </div>
            <div style="
              color: #666; 
              font-size: 14px;
            ">
              You save ₹${savingsAmount.toLocaleString()} (${this.bestDiscount.label})
            </div>
          </div>
          <div 
            class="cart-coupon-code-button" 
            onclick="window.cartDiscountCalculator.copyToClipboard('${this.bestDiscount.code}')"
            style="
              background: #fff; 
              border: 1px dashed #2e7d32; 
              border-radius: 6px; 
              padding: 8px 12px; 
              font-weight: bold; 
              color: #2e7d32;
              font-size: 14px;
              letter-spacing: 0.5px;
              cursor: pointer;
              transition: all 0.2s ease;
              user-select: none;
            "
            onmouseover="this.style.backgroundColor='#e8f5e8'; this.style.transform='scale(1.02)'"
            onmouseout="this.style.backgroundColor='#fff'; this.style.transform='scale(1)'"
            title="Click to copy coupon code"
          >
            ${this.bestDiscount.code}
          </div>
        </div>
      `;

      container.style.display = 'block';
    } else {
      this.log('No best discount to render');
      container.style.display = 'none';
    }
  }

  updateCartSubtotal(newSubtotal) {
    this.log('Updating cart subtotal to:', newSubtotal);
    this.cartSubtotal = newSubtotal;
    // Recompute items from window.cart if available
    this.getCartData();
    this.calculateBestDiscount();
    this.renderBestDiscount();
  }

  async copyToClipboard(code) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        this.showToast(`Coupon code "${code}" copied to clipboard!`);
        this.log('Coupon code copied to clipboard:', code);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          this.showToast(`Coupon code "${code}" copied to clipboard!`);
          this.log('Coupon code copied to clipboard (fallback):', code);
        } else {
          this.log('Failed to copy coupon code (fallback)');
        }
      }
    } catch (err) {
      this.log('Error copying to clipboard:', err);
    }
  }

  showToast(message, type = 'success') {
    const toastManager = document.querySelector('toast-message-manager');
    if (toastManager && typeof toastManager.pushMessage === 'function') {
      toastManager.pushMessage(message, type);
    } else {
      this.log('Toast message:', message);
    }
  }
}
  
  // Initialize the cart discount calculator when DOM is loaded
  document.addEventListener('DOMContentLoaded', function() {
    window.cartDiscountCalculator = new CartDiscountCalculator();
    window.cartDiscountCalculator.init();
    
    // Listen for cart updates to refresh the best coupon UI
    document.addEventListener('afterUpdateQuantity', function() {
      if (window.cartDiscountCalculator) {
        // Re-fetch cart data and recompute
        window.cartDiscountCalculator.getCartData();
        window.cartDiscountCalculator.calculateBestDiscount();
        window.cartDiscountCalculator.renderBestDiscount();
      }
    });

    // Listen for cart changes (when items are added to empty cart)
    document.addEventListener('afterCartChanged', function() {
      if (window.cartDiscountCalculator) {
        setTimeout(() => {
          window.cartDiscountCalculator.getCartData();
          window.cartDiscountCalculator.calculateBestDiscount();
          window.cartDiscountCalculator.renderBestDiscount();
        }, 100);
      }
    });
  });
  
  // Also initialize if DOM is already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      window.cartDiscountCalculator = new CartDiscountCalculator();
      window.cartDiscountCalculator.init();
      
      // Listen for cart updates to refresh the best coupon UI
      document.addEventListener('afterUpdateQuantity', function() {
        if (window.cartDiscountCalculator) {
          window.cartDiscountCalculator.getCartData();
          window.cartDiscountCalculator.calculateBestDiscount();
          window.cartDiscountCalculator.renderBestDiscount();
        }
      });

      // Listen for cart changes (when items are added to empty cart)
      document.addEventListener('afterCartChanged', function() {
        if (window.cartDiscountCalculator) {
          setTimeout(() => {
            window.cartDiscountCalculator.getCartData();
            window.cartDiscountCalculator.calculateBestDiscount();
            window.cartDiscountCalculator.renderBestDiscount();
          }, 100);
        }
      });
    });
  } else {
    window.cartDiscountCalculator = new CartDiscountCalculator();
    window.cartDiscountCalculator.init();
    
                 // Listen for cart updates to refresh the best coupon UI
      document.addEventListener('afterUpdateQuantity', function() {
        if (window.cartDiscountCalculator) {
          window.cartDiscountCalculator.getCartData();
          window.cartDiscountCalculator.calculateBestDiscount();
          window.cartDiscountCalculator.renderBestDiscount();
        }
      });

      // Listen for cart changes (when items are added to empty cart)
      document.addEventListener('afterCartChanged', function() {
        if (window.cartDiscountCalculator) {
          setTimeout(() => {
            window.cartDiscountCalculator.getCartData();
            window.cartDiscountCalculator.calculateBestDiscount();
            window.cartDiscountCalculator.renderBestDiscount();
          }, 100);
        }
      });
  } 